import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, ArrowUpDown, Inbox, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const LIMIT = 20;

const AdminFeedbackPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getFeedback(skip, LIMIT, sortDir);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Error fetching feedback:', err);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [skip, sortDir]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await adminApi.deleteFeedback(deleteId);
      setItems((prev) => prev.filter((f) => f.feedback_id !== deleteId));
      setTotal((prev) => prev - 1);
      toast.success('Feedback deleted');
    } catch (err) {
      console.error('Error deleting feedback:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete feedback');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + LIMIT, total);

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-feedback-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" data-testid="back-to-admin-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight">Feedback</h1>
            <p className="text-muted-foreground text-sm">Feedback submitted by logged-in users across the site</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setSkip(0); setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); }}
            className="gap-2"
            data-testid="toggle-sort-date-btn"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            Date {sortDir === 'asc' ? '(Oldest First)' : '(Newest First)'}
          </Button>

          <span className="text-sm text-muted-foreground ml-auto">
            {total === 0 ? '0 entries' : `${from}-${to} of ${total}`}
          </span>
        </div>

        <Card className="border-2">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center" data-testid="no-feedback-message">
                <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No feedback submissions yet.</p>
              </div>
            ) : (
              <Table data-testid="feedback-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="min-w-[220px]">Message</TableHead>
                    <TableHead>Page URL</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((f) => (
                    <TableRow key={f.feedback_id} data-testid={`feedback-row-${f.feedback_id}`}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDate(f.created_at)}
                      </TableCell>
                      <TableCell className="max-w-[140px] truncate">{f.name || '—'}</TableCell>
                      <TableCell className="max-w-[180px] truncate">{f.email || '—'}</TableCell>
                      <TableCell className="max-w-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground line-clamp-2">{f.message}</span>
                          {f.message.length > 120 && (
                            <Button
                              variant="link"
                              size="sm"
                              className="shrink-0 h-auto p-0"
                              onClick={() => setViewItem(f)}
                              data-testid={`view-feedback-${f.feedback_id}`}
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[160px] truncate text-xs text-muted-foreground font-mono">
                        {f.page_url}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(f.feedback_id)}
                          data-testid={`delete-feedback-${f.feedback_id}`}
                          aria-label="Delete feedback"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {total > LIMIT && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0}
              onClick={() => setSkip((s) => Math.max(0, s - LIMIT))}
              data-testid="feedback-prev-page-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={skip + LIMIT >= total}
              onClick={() => setSkip((s) => s + LIMIT)}
              data-testid="feedback-next-page-btn"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* View full message dialog */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase">Feedback</DialogTitle>
            <DialogDescription>
              {viewItem && `Submitted ${formatDate(viewItem.created_at)}`}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm whitespace-pre-wrap">{viewItem?.message}</p>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">Delete Feedback?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this feedback? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-feedback-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminFeedbackPage;
